/**
 * StrategyWorkspace — v3.18.0
 *
 * The Strategy stage page sits between Go/No-Go and Drafting in the
 * federal pursuit lifecycle. Phase 1 ships two artifacts:
 *
 *   1) Win Theme Registry — CRUD per pursuit, auto-numbered T1..Tn,
 *      with discriminator and ghost tagging.
 *   2) Section M Evaluation Factors — AI-extract via the Shredder
 *      pipeline + user-confirm form. Captures factor labels, weights,
 *      and nested sub-factors.
 *
 * Route: /:workspaceId/pursuit/:pursuitId/strategy
 */
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Sparkles, Layers, BarChart3, ClipboardList, CalendarDays, BookOpen, ChevronDown, ChevronRight } from 'lucide-react';
import WinThemeRegistry from '../../components/strategy/WinThemeRegistry';
import ThemeCoverageHeatmap from '../../components/strategy/ThemeCoverageHeatmap';
import AuthorAssignmentMatrix from '../../components/strategy/AuthorAssignmentMatrix';
import ColorTeamSchedule from '../../components/strategy/ColorTeamSchedule';
import VolumeIIBoilerplate from '../../components/strategy/VolumeIIBoilerplate';
import PursuitNextStepFooter from '../../components/pursuit/PursuitNextStepFooter';
import PursuitLifecycleStepper from '../../components/pursuit/PursuitLifecycleStepper';
import { useAuth } from '../../contexts/AuthContext';
import { usePursuit } from '../../contexts/PursuitContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function StrategyWorkspace() {
  const { pursuitId } = useParams();
  const { getAuthHeaders } = useAuth();
  const { enterPursuit } = usePursuit();
  const [pursuit, setPursuit] = useState(null);
  const [authMatrixOpen, setAuthMatrixOpen] = useState(true);

  // Mirror the pattern used by GoNoGoDashboardLive / OpportunityIntakeLive —
  // fetch the pursuit and call enterPursuit() so the sidebar switches to
  // pursuit context (Active Pursuit header + pursuit menu).
  useEffect(() => {
    if (!pursuitId) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API_URL}/api/pursuits/${pursuitId}`, { headers: getAuthHeaders() });
        if (!res.ok) return;
        const data = await res.json();
        if (cancelled) return;
        setPursuit(data);
        enterPursuit({
          id: data.id,
          title: data.opportunity_name,
          rfpNumber: data.solicitation_number || data.agency_code,
          dueDate: data.response_deadline,
        });
      } catch (err) {
        // v3.20.1 — surface to console; pursuit-bar HUD population is non-fatal
        // but a silent swallow used to hide auth/network failures during dev.
        if (!cancelled) console.warn('[StrategyWorkspace] HUD pursuit load failed:', err);
      }
    })();
    return () => { cancelled = true; };
  }, [pursuitId, getAuthHeaders, enterPursuit]);

  return (
    <div className="min-h-screen bg-slate-50" data-testid="strategy-workspace">
      {/* Header — v3.18.24 canonical: px-8 py-4 + max-w-7xl mx-auto */}
      <div className="bg-white border-b border-slate-200 px-8 py-4">
        <div className="max-w-7xl mx-auto flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-violet-100 to-fuchsia-100 flex items-center justify-center flex-shrink-0">
              <Sparkles className="w-5 h-5 text-violet-700" />
            </div>
            <div>
              <h1
                className="text-2xl font-semibold text-slate-900"
                style={{ fontFamily: 'Manrope, sans-serif' }}
              >
                Strategy
              </h1>
              {pursuit?.opportunity_name && (
                <p className="text-xs text-slate-500 mt-0.5" data-testid="strategy-pursuit-name">
                  {pursuit.opportunity_name}
                </p>
              )}
              <p className="text-sm text-slate-500 mt-1">
                Win plan before the pen hits the page — themes you'll thread through every volume, and the
                evaluation factors the customer will actually score against.
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="px-8">
        <div className="max-w-7xl mx-auto">
          <PursuitLifecycleStepper />
        </div>
      </div>

      <div className="p-8">
        <div className="max-w-7xl mx-auto space-y-6">

          <div className="grid grid-cols-1 gap-6">
        <section data-testid="strategy-win-themes-section">
          <div className="flex items-center gap-2 mb-3">
            <Layers className="w-5 h-5 text-violet-600" />
            <h2
              className="text-lg font-bold text-slate-900"
              style={{ fontFamily: 'Manrope, sans-serif' }}
            >
              Win Theme Registry
            </h2>
            <span className="text-xs text-slate-400 ml-auto italic">
              Auto-numbered T1, T2, T3… Tag each theme as a discriminator or ghost.
            </span>
          </div>
          <WinThemeRegistry pursuitId={pursuitId} />
        </section>

        {/* Section M (Submittal Volumes + Evaluation Factors) moved to
            Manage Documents, co-located with the Compliance Matrix it feeds. */}

        {/* v3.18.2 — Theme Coverage Heatmap (Win Themes × proposal sections) */}
        <section data-testid="strategy-theme-coverage-section">
          <div className="flex items-center gap-2 mb-3">
            <BarChart3 className="w-5 h-5 text-fuchsia-600" />
            <h2
              className="text-lg font-bold text-slate-900"
              style={{ fontFamily: 'Manrope, sans-serif' }}
            >
              Theme Coverage
            </h2>
            <span className="text-xs text-slate-400 ml-auto italic">
              Which themes are showing up across proposal sections.
            </span>
          </div>
          <ThemeCoverageHeatmap pursuitId={pursuitId} />
        </section>

        {/* v3.18.8 — Author Assignment Matrix (Phase 2) */}
        <section data-testid="strategy-author-matrix-section">
          <button
            type="button"
            onClick={() => setAuthMatrixOpen(v => !v)}
            className="w-full flex items-center gap-2 mb-3 text-left"
            data-testid="strategy-author-matrix-toggle"
          >
            {authMatrixOpen
              ? <ChevronDown className="w-4 h-4 text-slate-400" />
              : <ChevronRight className="w-4 h-4 text-slate-400" />}
            <ClipboardList className="w-5 h-5 text-emerald-600" />
            <h2
              className="text-lg font-bold text-slate-900"
              style={{ fontFamily: 'Manrope, sans-serif' }}
            >
              Author Assignment Matrix
            </h2>
            <span className="text-xs text-slate-400 ml-auto italic">
              One owner per section, with a hard due date. Overdue rows turn red.
            </span>
          </button>
          {authMatrixOpen && <AuthorAssignmentMatrix pursuitId={pursuitId} />}
        </section>

        {/* v3.18.8 — Color Team Schedule (Phase 2) */}
        <section data-testid="strategy-color-team-section">
          <div className="flex items-center gap-2 mb-3">
            <CalendarDays className="w-5 h-5 text-rose-600" />
            <h2
              className="text-lg font-bold text-slate-900"
              style={{ fontFamily: 'Manrope, sans-serif' }}
            >
              Color Team Schedule
            </h2>
            <span className="text-xs text-slate-400 ml-auto italic">
              Pink / Red / Gold review milestones with assigned reviewers.
            </span>
          </div>
          <ColorTeamSchedule pursuitId={pursuitId} />
        </section>

        {/* v3.18.14 — Strategy Phase 3A: Volume II Business Boilerplate
            v3.18.15 — Volume IV Cost Boilerplate added (volume tabs) */}
        <section data-testid="strategy-volume-ii-section">
          <div className="flex items-center gap-2 mb-3">
            <BookOpen className="w-5 h-5 text-indigo-600" />
            <h2
              className="text-lg font-bold text-slate-900"
              style={{ fontFamily: 'Manrope, sans-serif' }}
            >
              Business &amp; Cost Volume Boilerplate
            </h2>
            <span className="text-xs text-slate-400 ml-auto italic">
              Reusable Volume II / Volume IV sections you can pull into any pursuit.
            </span>
          </div>
          <VolumeIIBoilerplate pursuitId={pursuitId} />
        </section>
      </div>

      <PursuitNextStepFooter currentStep="strategy" />
        </div>
      </div>
    </div>
  );
}
